import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { LoaderGlobal } from '@thoughtspot/radiant-react/widgets/loading-indicator/loader-global';
import { Radio } from '@thoughtspot/radiant-react/widgets/radio';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { useGetLlmProviderConfigLazyQuery, useGetLlmProviderModelsLazyQuery, useGetLlmProvidersQuery, } from "../../../services/llm/llm-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { emptyFormState, PROVIDER_CONFIG, } from './llm-form-constants';
import styles from './llm-form-styles.module.scss';
import { getLlmNamingConvection } from './llm-form-utils';
export const LlmForm = ({ onFormChange, providerSelected, isCustomerLLMAccount = false, formErrors, setLlmFormErrors, }) => {
    const { t } = useTranslation();
    const { metricsService } = BasePageContext();
    const [llmProviderSelected, setLlmProviderSelected] = useState('');
    const [isCheckedCustomAcct, setIsCheckedCustomAcct] = useState(isCustomerLLMAccount);
    const [modelVersion, setModelVersion] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [endpoint, setEndpoint] = useState('');
    const [region, setRegion] = useState('');
    const [passphrase, setPassphrase] = useState('');
    const [user, setUser] = useState('');
    const [account, setAccount] = useState('');
    const [internalFormErrors, setInternalFormErrors] = useState({
        ...emptyFormState,
    });
    const { data: llmProviders, loading: isProvidersLoading, error, } = useGetLlmProvidersQuery();
    const { getLlmProviderModels, data: llmModelVersion, loading: isModelVersionsLoading, } = useGetLlmProviderModelsLazyQuery(llmProviderSelected);
    const { getLlmProviderConfig, data: providerConfigs, loading: isConfigLoading, error: configError, } = useGetLlmProviderConfigLazyQuery(llmProviderSelected);
    useEffect(() => {
        setInternalFormErrors(formErrors);
    }, [formErrors]);
    const updateFormData = useCallback(() => {
        const formData = {
            provider: getLlmNamingConvection().get(llmProviderSelected),
            model: modelVersion,
            apiKey,
            endpoint,
            usecustomaccount: isCheckedCustomAcct,
            region,
            passphrase,
            user,
            account,
        };
        onFormChange(formData);
    }, [
        llmProviderSelected,
        modelVersion,
        apiKey,
        endpoint,
        isCheckedCustomAcct,
        region,
        passphrase,
        user,
        account,
        onFormChange,
    ]);
    useEffect(() => {
        updateFormData();
    }, [llmProviderSelected, modelVersion, isCheckedCustomAcct, apiKey]);
    useEffect(() => {
        metricsService.collectEvent(new UserAction(userActionTypeEnum.LLM_CONFIGURATION_OPEN), {});
    }, []);
    useEffect(() => {
        if (!providerSelected && llmProviders?.length) {
            setLlmProviderSelected(PROVIDER_CONFIG[llmProviders[0]]?.id);
        }
        if (providerSelected)
            setLlmProviderSelected(PROVIDER_CONFIG[providerSelected]?.id);
    }, [llmProviders, providerSelected]);
    useEffect(() => {
        if (llmProviderSelected?.length) {
            getLlmProviderModels();
            getLlmProviderConfig();
        }
    }, [llmProviderSelected]);
    const clearStateValues = () => {
        setApiKey('');
        setEndpoint('');
        setRegion('');
        setPassphrase('');
        setUser('');
        setAccount('');
    };
    useEffect(() => {
        const configData = providerConfigs?.Admin_getLLMProviderConfig?.Data
            ?.default;
        if (isCheckedCustomAcct && configData?.provider?.length) {
            setApiKey(configData.apiKey || '');
            setModelVersion(configData.model);
            setEndpoint(configData.endpoint || '');
            setRegion(configData.region || '');
            setPassphrase(configData.passphrase || '');
            setUser(configData.user || '');
            setAccount(configData.account || '');
        }
        if (!isCheckedCustomAcct)
            clearStateValues();
    }, [providerConfigs, isCheckedCustomAcct]);
    const modelOptions = useMemo(() => {
        const modelVersions = llmModelVersion?.Admin_getLLMProviderModels?.Data;
        if (!modelVersions) {
            return [
                {
                    options: [],
                },
            ];
        }
        const options = modelVersions?.map(model => ({
            id: model,
            label: model.replace(/_/g, ' '),
        }));
        return [
            {
                options,
            },
        ];
    }, [llmModelVersion]);
    useEffect(() => {
        if (modelOptions.length) {
            setModelVersion(modelOptions[0]?.options?.[0]?.id || '');
        }
    }, [modelOptions]);
    const onChangeLlmProvider = (e) => {
        const value = _.get(e, 'target.value');
        setLlmProviderSelected(value);
        if (value !== providerSelected)
            setIsCheckedCustomAcct(false);
        else
            setIsCheckedCustomAcct(isCustomerLLMAccount);
        clearStateValues();
        getLlmProviderModels();
        setLlmFormErrors({});
    };
    const showLoader = isProvidersLoading ||
        (llmProviderSelected === providerSelected &&
            isCustomerLLMAccount &&
            (isConfigLoading || isModelVersionsLoading));
    return (React.createElement(LoaderGlobal, { hideLoader: !showLoader, className: styles.connectionTableContainer },
        React.createElement(Vertical, null,
            React.createElement(Radio.Group, { id: "llm", selectedValue: llmProviderSelected, onChange: onChangeLlmProvider, orientation: "tray", hideRadio: true, "data-testid": "llm-provider-group" }, llmProviders?.map(providerName => {
                const config = PROVIDER_CONFIG[providerName];
                return (React.createElement(Radio, { key: config.id, id: config.id, label: providerName, value: config.id, isSideLabel: true, className: styles.radioButton, subTitle: config.isEarlyAccess
                        ? t('orion.aiModelType.earlyAccess')
                        : undefined, imageUrl: config.imageUrl, "data-testid": `llm-provider-${config.id}` }));
            })))));
};
//# sourceMappingURL=llm-form.container.js.map